import { EXAMPLE_GROUP_ID } from './exampleGroup';

/**
 * The example group is generated per device language and rotates one
 * scenario per app launch, but its detail screens read the React Query
 * cache — persisted for 30 days on both platforms. Invalidating only marks
 * a query stale: the old payload is still handed to the screen first and
 * swapped once the refetch lands, so a user tapping "Apartment 5B" would
 * see a "Weekend in Barcelona" header for a frame or two. The example's
 * queries are therefore RESET (back to no data; active ones refetch at
 * once and `withExampleGroup` answers immediately). `useExampleGroupSession`
 * calls this on launch and on language change.
 *
 * Every id the fixture hands out is `example` or `example-…`
 * (`example-expense-3`, `example-ana`, `example-settlement-1`). Real ids
 * are ULIDs/UUIDs and cannot collide with that prefix.
 */

const EXAMPLE_ID_PREFIX = `${EXAMPLE_GROUP_ID}-`;

function isExampleId(value: unknown): boolean {
  return (
    typeof value === 'string' &&
    (value === EXAMPLE_GROUP_ID || value.startsWith(EXAMPLE_ID_PREFIX))
  );
}

/**
 * True when a React Query key refers to the example group or one of its
 * entities — i.e. one of its STRING segments is an example id. Filter
 * objects are deliberately not inspected: `{ search: 'example' }` is a
 * user typing the word "example" into a search box, not the example
 * group, and resetting those would refetch real data for nothing.
 */
export function isExampleGroupQueryKey(queryKey: readonly unknown[]): boolean {
  return queryKey.some(isExampleId);
}

/**
 * Structural subset of `QueryClient` — core has no react-query
 * dependency, and this is all the helper needs.
 */
export interface ExampleQueryClient {
  resetQueries(filters: {
    predicate: (query: { queryKey: readonly unknown[] }) => boolean;
  }): Promise<void>;
}

/** Drops every cached example-group query (active ones refetch at once). */
export function resetExampleGroupQueries(
  client: ExampleQueryClient
): Promise<void> {
  return client.resetQueries({
    predicate: query => isExampleGroupQueryKey(query.queryKey),
  });
}
